import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { useRouteLoaderData } from '@remix-run/react';

import StaticPage from '~/components/StaticPage';
import { staticPageClientLoader } from '~/components/StaticPage/staticPageClientLoader';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { RootLoaderData } from '~/root';
import { findArchivePaginationData, PAGE_PARAM } from '~/utils/archivePagination';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getStaticPageMetaData from '~/utils/getStaticPageMetaData';
import setCacheHeaders, { BASE_CACHE_TAG } from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import type { StaticPageLoaderData } from '../services/static_page';
import { staticPageLoader } from '../services/static_page';

const getArchivePageNumber = (requestUrl?: string): number => {
  if (!requestUrl) return 1;
  try {
    const parsed = Number.parseInt(new URL(requestUrl).searchParams.get(PAGE_PARAM) || '1', 10);
    return Number.isNaN(parsed) ? 1 : parsed;
  } catch {
    return 1;
  }
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backward compatibility
export const headers: HeadersFunction = ({ actionHeaders, loaderHeaders, parentHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': loaderHeaders.get('Cache-Tag') || BASE_CACHE_TAG,
  };
};

export const meta: MetaFunction<typeof staticPageLoader> = (args) => {
  const metaData = getStaticPageMetaData(args);

  const pageNumber = getArchivePageNumber(args.data?.requestUrl);
  if (pageNumber <= 1) return metaData;

  const canonicalBase = getCanonicalUrl(args.data?.requestUrl);
  if (!canonicalBase) return metaData;

  const paginatedUrl = `${canonicalBase}?${PAGE_PARAM}=${pageNumber}`;

  return metaData.map((item: Record<string, unknown>) => {
    if (item.rel === 'canonical') return { ...item, href: paginatedUrl };
    if (item.property === 'og:url') return { ...item, content: paginatedUrl };
    if (item.name === 'twitter:url') return { ...item, content: paginatedUrl };
    return item;
  });
};
export const loader: LoaderFunction = async ({ request, context }) => {
  const requestedPage = getArchivePageNumber(request.url);

  return staticPageLoader({
    request,
    context,
    pageRequestParams: {
      pathname: 'archive',
      page: String(requestedPage),
    },
    validatePage: (page) => {
      const pagination = findArchivePaginationData(page.viewable_page_version?.content);
      if (!pagination) return;

      const isPastLastPage = requestedPage > Math.max(pagination.totalPages, 1);
      if (isPastLastPage && pagination.postCount === 0) {
        throw new Response(null, {
          status: 404,
          statusText: 'Not Found',
          headers: setCacheHeaders({ sMaxAge: 60 * 60 }),
        });
      }
    },
  });
};

export const clientLoader = staticPageClientLoader;

const Archive = () => {
  const { page } = useTypedLoaderData<StaticPageLoaderData>();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  return <StaticPage page={page} site={site} />;
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Archive;
